import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bot, Phone, Sparkles, 
  CheckCircle2,
  Shield, FileText, Activity, Send, Zap, Users, Check, PhoneOutgoing, PhoneIncoming
} from 'lucide-react';

const conversations = [
  { 
    userMessage: "Hey Asha, can you take care of all my EOBs from this week?",
    agent: "Alisha", 
    role: "EOB Posting", 
    color: "from-emerald-500 to-teal-600", 
    image: "/assets/alisha.png",
    response: "On it! I'll process all 47 EOBs from this week — posting payments, adjustments, and flagging any discrepancies for your review.",
  },
  { 
    userMessage: "Hey Asha, can you verify insurance for all of tomorrow's patients?",
    agent: "Amanda", 
    role: "Insurance Verification", 
    color: "from-blue-500 to-indigo-600", 
    image: "/assets/amanda.png",
    response: "Running verifications for 23 patients scheduled tomorrow. I'll update eligibility, benefits, and copay estimates in each chart.",
  },
  { 
    userMessage: "Hey Asha, can you call my unaccepted cases from last month?",
    agent: "Kim", 
    role: "Case Acceptance", 
    color: "from-pink-500 to-rose-600", 
    image: "/assets/kim.png",
    response: "I found 14 patients with unaccepted treatment plans from last month. I'll call each one to discuss their plan, answer questions, and help them schedule.",
  },
  { 
    userMessage: "Hey Asha, can you scrub and submit all my pending claims?",
    agent: "Alayna", 
    role: "Claim Scrubbing", 
    color: "from-violet-500 to-purple-600", 
    image: "https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png",
    response: "Found 31 pending claims. I'll scrub each one for errors, attach narratives where needed, and submit them electronically.",
  },
  { 
    userMessage: "Hey Asha, can you call patients to confirm their appointments this week?",
    agent: "Amy", 
    role: "Receptionist", 
    color: "from-rose-500 to-pink-600", 
    image: "/assets/amy.png",
    response: "I see 67 appointments this week. I'll call each patient to confirm, handle any reschedules, and update the schedule in real time.",
  },
  { 
    userMessage: "Hey Asha, can you call insurance on all my outstanding claims?",
    agent: "Laura", 
    role: "Insurance Calls", 
    color: "from-cyan-500 to-blue-600", 
    image: "https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png",
    response: "I see 18 outstanding claims that need follow-up. I'll call each payer, get status updates, and escalate any that need appeals.",
  },
  { 
    userMessage: "Hey Asha, can you dial patients who are overdue for hygiene?",
    agent: "Stephanie", 
    role: "Hygiene Recall", 
    color: "from-amber-500 to-orange-600", 
    image: "/assets/stephanie.png",
    response: "I found 89 patients overdue for hygiene. Starting outbound calls now — I'll book them into your open hygiene slots this month.",
  },
  { 
    userMessage: "Hey Asha, can you send billing statements to all patients with a balance?",
    agent: "Kianna", 
    role: "Patient Billing", 
    color: "from-teal-500 to-cyan-600", 
    image: "/assets/kianna.png",
    response: "I found 52 patients with outstanding balances. I'll generate and send itemized statements, and set up payment plan options for larger amounts.",
  },
  { 
    userMessage: "Hey Asha, can you follow up on all denied claims and file appeals?",
    agent: "Sarah", 
    role: "Denial Management", 
    color: "from-indigo-500 to-violet-600", 
    image: "/assets/sarah.png",
    response: "I see 9 denied claims this month. I'll review each denial reason, correct any issues, and submit appeals with supporting documentation.",
  },
  { 
    userMessage: "Hey Asha, can you answer all my incoming calls today?",
    agent: "Amy", 
    role: "Receptionist", 
    color: "from-rose-500 to-pink-600", 
    image: "/assets/amy.png",
    response: "I've got the phones! I'll answer every call, schedule appointments, handle patient questions, and route urgent matters to your team.",
  },
  { 
    userMessage: "Hey Asha, can you collect on all outstanding patient balances?",
    agent: "Julia", 
    role: "A/R Collections", 
    color: "from-green-500 to-emerald-600", 
    image: "/assets/julia.png",
    response: "Reviewing $48.2K in outstanding balances. I'll send statements, make collection calls, and set up payment plans where needed.",
  },
  { 
    userMessage: "Hey Asha, can you get intake forms from all of tomorrow's new patients?",
    agent: "Morgan", 
    role: "Patient Intake", 
    color: "from-sky-500 to-blue-600", 
    image: "/assets/morgan.png",
    response: "I see 6 new patients tomorrow. I'll text each one a link to complete their forms and upload insurance cards before they arrive.",
  },
];

const features = [
  "AI Insurance Verification",
  "AI EOB Posting",
  "AI Claim Generation",
  "AI Inbound Calls",
  "AI Outbound Calls",
  "AI SMS & Messaging",
  "AI Hygiene Recall",
  "AI Case Acceptance",
  "AI Denial Mgmt",
  "AI A/R Collections",
  "AI Patient Billing",
  "AI Patient Intake",
];

const MobileAdHeroSection = () => {
  const [currentConvo, setCurrentConvo] = useState(0);
  const [phase, setPhase] = useState('typing');
  const [displayText, setDisplayText] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [phase, displayText]);

  useEffect(() => {
    const convo = conversations[currentConvo];
    let charIndex = 0;
    let typingTimer;
    let phaseTimer;

    if (phase === 'typing') {
      setDisplayText('');
      typingTimer = setInterval(() => {
        charIndex++;
        setDisplayText(convo.userMessage.slice(0, charIndex));
        if (charIndex >= convo.userMessage.length) {
          clearInterval(typingTimer);
          phaseTimer = setTimeout(() => setPhase('delegating'), 500);
        }
      }, 30);
    } else if (phase === 'delegating') {
      phaseTimer = setTimeout(() => setPhase('working'), 800);
    } else if (phase === 'working') {
      phaseTimer = setTimeout(() => setPhase('complete'), 2000);
    } else if (phase === 'complete') {
      phaseTimer = setTimeout(() => setPhase('pause'), 2500);
    } else if (phase === 'pause') {
      phaseTimer = setTimeout(() => { 
        setCurrentConvo(prev => (prev + 1) % conversations.length); 
        setPhase('typing'); 
      }, 600);
    }

    return () => { clearInterval(typingTimer); clearTimeout(phaseTimer); };
  }, [currentConvo, phase]);

  const current = conversations[currentConvo];

  return (
    <section className="relative overflow-hidden bg-[#0A0A12] min-h-screen">
      {/* Background gradient layers */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A12] via-[#0d1224] to-[#0A0A12]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/8 rounded-full blur-[120px]" />
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-indigo-600/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-5 pt-20 pb-24">
        {/* Asha Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-2.5 mb-6"
        >
          <img src="https://customer-assets.emergentagent.com/job_dentalcopy/artifacts/si20q9wb_alogo.png" alt="Asha Dental Logo" className="h-8 w-auto" />
          <span className="font-semibold text-lg text-white">Asha Dental</span>
        </motion.div>

        {/* Hero Text - Mobile Centered */}
        <div className="text-center mb-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-[2.5rem] sm:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white mb-6"
          >
            20+ Dental AI Products in{' '}
            <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
              1 Platform
            </span>
          </motion.h1>

          {/* Feature checklist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-3 gap-x-2 gap-y-2 text-left max-w-md mx-auto"
          >
            {features.map((feature, i) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.35 + i * 0.05 }}
                className="flex items-center gap-2"
              >
                <div className="w-4 h-4 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5 text-blue-400" />
                </div>
                <span className="text-[11px] text-white/70 font-medium">{feature}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Chat Interface - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm shadow-2xl shadow-black/30">
            {/* Top Bar */}
            <div className="flex items-center justify-center px-4 py-2.5 border-b border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-medium bg-white/[0.04] text-white/40">
                <Bot className="w-3 h-3" />
                <span>Asha</span>
                <div className="flex items-center gap-1 ml-1.5 px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[9px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Online
                </div>
              </div>
            </div>

            {/* Chat Messages Area */}
            <div className="p-4 space-y-3 h-[280px] overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              {/* User message bubble */}
              <div className="flex justify-end">
                <motion.div
                  key={`user-${currentConvo}-${phase}`}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="max-w-[85%] px-4 py-3 rounded-2xl rounded-br-md bg-blue-600 text-white"
                >
                  <p className="text-sm leading-relaxed">
                    {displayText}
                    {phase === 'typing' && (
                      <motion.span 
                        animate={{ opacity: [1, 0] }} 
                        transition={{ duration: 0.5, repeat: Infinity }} 
                        className="inline-block w-0.5 h-4 bg-white/80 ml-0.5 align-middle" 
                      />
                    )}
                  </p>
                </motion.div>
              </div>

              {/* Asha delegating message */}
              <AnimatePresence>
                {(phase === 'delegating' || phase === 'working' || phase === 'complete') && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex gap-2.5"
                  >
                    <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="max-w-[85%]">
                      <div className="text-[10px] font-semibold text-white/30 mb-1 ml-1">Asha</div>
                      <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/[0.08]">
                        <p className="text-sm text-white/80 leading-relaxed">
                          Delegating to <span className="text-white font-semibold">{current.agent}</span>
                          <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold uppercase tracking-wider inline-flex items-center gap-0.5">
                            <Bot className="w-2.5 h-2.5" /> AI Agent
                          </span>
                          <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-white/[0.06] text-white/40 font-medium">{current.role}</span>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Agent working / response */}
              <AnimatePresence>
                {(phase === 'working' || phase === 'complete') && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="flex gap-2.5"
                  >
                    <div className="relative shrink-0">
                      <img 
                        src={current.image} 
                        alt={current.agent} 
                        className="w-7 h-7 rounded-full object-cover ring-2 ring-blue-400/20" 
                      />
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-[1.5px] border-[#0A0A12]">
                        <Sparkles className="w-1.5 h-1.5 text-white" />
                      </div>
                    </div>
                    <div className="max-w-[85%]">
                      <div className="text-[10px] font-semibold text-white/30 mb-1 ml-1">{current.agent}</div>
                      <div className={`px-4 py-3 rounded-2xl rounded-bl-md border ${
                        phase === 'complete' 
                          ? 'bg-green-500/[0.06] border-green-500/20' 
                          : 'bg-white/[0.04] border-white/[0.08]'
                      }`}>
                        {phase === 'working' ? (
                          <div className="flex items-center gap-2 text-sm text-white/60">
                            <motion.div 
                              animate={{ rotate: 360 }} 
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            >
                              <Zap className="w-3.5 h-3.5 text-blue-400" />
                            </motion.div>
                            <span>Working on it</span>
                            <div className="flex gap-0.5">
                              {[0, 150, 300].map(delay => (
                                <div 
                                  key={delay} 
                                  className="w-1 h-1 rounded-full bg-blue-400 animate-bounce" 
                                  style={{ animationDelay: `${delay}ms` }} 
                                />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-white/80 leading-relaxed">{current.response}</p>
                            <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/[0.06]">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                              <span className="text-[11px] text-green-400 font-semibold">Handled automatically</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div ref={chatEndRef} />
            </div>

            {/* Input bar */}
            <div className="px-4 py-3 border-t border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                <span className="text-sm text-white/25 flex-1">Ask Asha anything...</span>
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                  <Send className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MobileAdHeroSection;
